import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import styled from '@emotion/styled';
import MarketplaceLayout from '@fuww/library/src/Layouts/MarketplacePage';
import Body1 from '@fuww/library/src/Typography/Body/Body1';
import Button from '@fuww/library/src/Button';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import PageCardContainer from '@fuww/library/src/PageCardContainer';
import TextWithCard from '@fuww/library/src/TextWithCard';
import TextCard from '@fuww/library/src/TextCard';
import SpacingContainer from '@fuww/library/src/SpacingContainer';
import Segment from '@fuww/library/src/Marketplace/Segment';
import ImageContainer from '@fuww/library/src/Marketplace/ImageContainer';
import AdaptiveCard from '@fuww/library/src/Cards/AdaptiveCard';
import LinkAndAnchor from '../components/LinkAndAnchor';
import messages from '../lib/messages.json';
import Layout from '../components/Layout';
import Head from '../components/Head';
import Hero from '../components/Hero';
import marketplaceSegments from '../data/marketplace/marketplaceSegments';
import hotTrends from '../data/marketplace/hotTrends.json';
import partnerSpotlight from '../data/marketplace/partnerSpotlight.json';
import {
  marketplaceCardImageConfigurations,
} from '../lib/imageConfigurations';
import getProxiedImages from '../lib/getProxiedImages';
import ImageWithUrls from '../components/ImageWithUrls';
import HomeLink from '../components/HomeLink';
import loading from '../lib/loading';
import InView from '../components/InView';

const DynamicSeasonalNews = dynamic(
  () => import('../components/Marketplace/SeasonalNews'),
  {
    ssr: false,
    loading,
  },
);

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  @media (min-width: ${breakpoints.sm}px) {
    flex-direction: row;
    justify-content: center;
    gap: 8px;
  }
`;

const seasonalInfo = {
  current: {
    title: 'Fall / Winter 2025',
    slug: 'fw25',
    keywords: ['fw25'],
  },
  upcoming: {
    title: 'Spring / Summer 2026',
    slug: 'ss26',
    keywords: ['ss26'],
  },
};

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/hkUum3XpChLv_-Ax_eljdAApQGrryXCvnhQG_RoJSDg/resize:fill-down:42:34:0/gravity:no/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvbWFya2V0cGxhY2UtaGVyby1tb2JpbGUuanBn',
    'https://r.fashionunited.com/Sz0Au7H9-36m6nUHM2sU1m8NCiPrAl4A_l7s_0De1xM/resize:fill-down:360:288:0/gravity:no/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvbWFya2V0cGxhY2UtaGVyby1tb2JpbGUuanBn',
    'https://r.fashionunited.com/JvrQbngdU_WBTJbdDFPO4PcJ8vATGU9eny3VHSxb-qE/resize:fill-down:720:576:0/gravity:no/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvbWFya2V0cGxhY2UtaGVyby1tb2JpbGUuanBn',
  ],
  desktop: [
    'https://r.fashionunited.com/moKBzMluATS-ui8ZXedZx0iKDunIdyCdRgApzzwZtIA/resize:fill-down:42:8:0/gravity:no/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvbWFya2V0cGxhY2UtaGVyby1iaWcuanBn',
    'https://r.fashionunited.com/129dm0Kiv5vdaJntd0B-RCwVGj76ZuygW8XEqG1Oy4M/resize:fill-down:1350:277:0/gravity:no/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvbWFya2V0cGxhY2UtaGVyby1iaWcuanBn',
    'https://r.fashionunited.com/X0D7NldHXMBFjyJBMOZkzPTm5ne5SkcsZTCnNwIYov8/resize:fill-down:2560:526:0/gravity:no/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvbWFya2V0cGxhY2UtaGVyby1iaWcuanBn',
    'https://r.fashionunited.com/6fvgCoXls7mm2csbvmFG00ZUEu5USLhUThmBXsIu4RI/resize:fill-down:5120:1052:0/gravity:no/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvbWFya2V0cGxhY2UtaGVyby1iaWcuanBn',
  ],
};

const partnerSpotlightCards = partnerSpotlight.map(({
  title,
  params,
  cardImageUrls: originalImageUrls,
}) => {
  const { imageUrls, srcSet } = getProxiedImages(
    { imageConfigurations: marketplaceCardImageConfigurations },
    originalImageUrls,
  );

  return (
    <LinkAndAnchor
      key={title}
      route="products"
      params={params}
    >
      <AdaptiveCard
        title={title}
        textAlign="center"
        image={imageUrls[0] && (
        <ImageWithUrls
          sizes={`
          (min-width: 1440px) 318px,
          (min-width: ${breakpoints.lg}px) calc(25vw - 42px),
          (min-width: ${breakpoints.md}px) calc(50vw - 52px),
          calc(100vw - 72px)
        `}
          srcSet={srcSet}
          imageUrls={imageUrls}
          alt=""
          disableRoundedCorners
        />
        )}
        aspectRatioPadding="150%"
      />
    </LinkAndAnchor>
  );
});

const MarketplacePage = () => {
  const intl = useIntl();

  const breadcrumbs = [
    <HomeLink />,
    'Marketplace',
  ];

  return (
    <>
      <Head
        title={intl.formatMessage(
          messages['marketplace.title'],
        )}
        description={intl.formatMessage(
          messages['marketplace.description'],
        )}
      />
      <MarketplaceLayout
        breadcrumbs={breadcrumbs}
        intro={(
          <>
            <SpacingContainer />
            <Body1>
              {intl.formatMessage(messages['marketplace.description'])}
            </Body1>
            <SpacingContainer />
            <ButtonContainer>
              <LinkAndAnchor
                route="products"
                params={{ marketId: '1' }}
              >
                <Button variant="text">
                  {intl.formatMessage(messages['marketplace.buttonLabel'])}
                </Button>
              </LinkAndAnchor>
              <LinkAndAnchor href="/brands">
                <Button variant="text">
                  {intl.formatMessage(messages['marketplace.brandDirectory'])}
                </Button>
              </LinkAndAnchor>
              <LinkAndAnchor href="/specials/fashion-brands-radar">
                <Button variant="text">
                  {intl.formatMessage(messages['marketplace.brandRadar'])}
                </Button>
              </LinkAndAnchor>
              <LinkAndAnchor
                href="/specials/fall-winter-2026-fashion-trends"
              >
                <Button variant="text">
                  {intl.formatMessage(messages['marketplace.fw26Trends'])}
                </Button>
              </LinkAndAnchor>
            </ButtonContainer>
          </>
        )}
        textWithCard={(
          <TextWithCard
            title={intl.formatMessage(
              messages['marketplace.apparelBuyers.title'],
            )}
            description={(
              <div
            // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage(
                    messages['marketplace.apparelBuyers.description'],
                  ),
                }}
              />
            )}
            callToAction={(
              <LinkAndAnchor
                route="products"
                params={{ marketId: '1' }}
              >
                <Button variant="outlined">
                  {intl.formatMessage(
                    messages['marketplace.apparelBuyers.callToAction'],
                  )}
                </Button>
              </LinkAndAnchor>
              )}
            card={(
              <TextCard
                title={intl.formatMessage(messages['marketplace.card.title'])}
                description={(
                  <div
                // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: intl.formatMessage(
                        messages['marketplace.card.description'],
                      ),
                    }}
                  />
                )}
                link={(
                  <LinkAndAnchor href="/marketplace/brands">
                    <Button variant="outlined">
                      {intl.formatMessage(
                        messages['marketplace.registerButtonText'],
                      )}
                    </Button>
                  </LinkAndAnchor>
                )}
              />
          )}
          />
        )}
        title={intl.formatMessage(
          messages['marketplace.title'],
        )}
        segments={marketplaceSegments.map(({
          title,
          route,
          params,
          icon,
        }) => (
          <LinkAndAnchor key={title} route={route} params={params}>
            <Segment icon={icon} title={title} />
          </LinkAndAnchor>
        ))}
        hotTrends={(
          <PageCardContainer pageCards={hotTrends.map(({
            title,
            params,
            cardImageUrls: originalCardImageUrls,
          }) => {
            const { imageUrls: cardImageUrls, srcSet } = getProxiedImages(
              { imageConfigurations: marketplaceCardImageConfigurations },
              originalCardImageUrls,
            );

            return (
              <ImageContainer key={title}>
                <LinkAndAnchor
                  route="products"
                  params={params}
                >
                  <ImageWithUrls
                    sizes={`(
                      min-width: ${breakpoints.md}px) calc((100vw - 160px) / 3
                    ),
                      (min-width: ${breakpoints.sm}px) calc((100vw - 84px) / 2),
                      calc(100vw - 64px)
                    `}
                    srcSet={srcSet}
                    imageUrls={cardImageUrls}
                    alt={title}
                    disableRoundedCorners
                  />
                </LinkAndAnchor>
                <Body1><strong>{title}</strong></Body1>
              </ImageContainer>
            );
          })}
          />
        )}
        partnerSpotlightCards={partnerSpotlightCards}
        featuredArticles={(
          <InView>
            <DynamicSeasonalNews seasonInfo={seasonalInfo.current} />
            <SpacingContainer />
            <DynamicSeasonalNews seasonInfo={seasonalInfo.upcoming} />
          </InView>
        )}
        segmentsHeader={intl.formatMessage(
          messages['marketplace.segmentsHeader'],
        )}
        hotTrendsHeader={intl.formatMessage(
          messages['marketplace.hotTrendsHeader'],
        )}
        partnerSpotlightHeader={intl.formatMessage(
          messages['marketplace.partnerSpotlightHeader'],
        )}
        featuredArticlesHeader={intl.formatMessage(
          messages['marketplace.featuredArticlesHeader'],
        )}
      />
    </>
  );
};

MarketplacePage.getLayout = (page: ReactElement) => (
  <Layout
    header={(
      <Hero
        alt="Marketplace banner image"
        imageUrls={headerImageUrls}
      />
    )}
  >
    {page}
  </Layout>
);

export default MarketplacePage;
